/**
 * /transporter — transporter self-service dashboard
 * ---------------------------------------------------
 * Resolved by email match against the `transporters` collection
 * (admin seeds your row with your email, you sign up at /register
 * with the same email, the backend resolver finds you on every
 * request). No separate auth flow.
 *
 * What you see here:
 *   - Your verified profile + lifetime stats
 *   - Active trips (status='booked' or 'in_transit') with a
 *     "Mark delivered" CTA that opens a photo upload modal
 *   - Completed trips (status='completed') for lifetime earnings
 *   - 403 if you aren't onboarded — clear "contact admin" hint
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import TransporterPayoutsBanner from "@/components/TransporterPayoutsBanner";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, CurrencyDollarIcon as CurrencyDollar, CheckCircleIcon as CheckCircle, MapPinIcon as MapPin, CameraIcon as Camera, ClockIcon as Clock, FilePdfIcon as FilePdf, DownloadSimpleIcon as DownloadSimple } from "@phosphor-icons/react";

export default function TransporterDashboard() {
  const [searchParams] = useSearchParams();
  // `?as=<email>` is the admin override path — passed back to the
  // server as a query param so the resolver swaps in that transporter.
  // Non-admins are silently ignored by the backend.
  const asEmail = searchParams.get("as") || "";
  const asParam = asEmail ? `?as_email=${encodeURIComponent(asEmail)}` : "";

  const [data, setData] = useState(null);
  const [openRequests, setOpenRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [res, openRes] = await Promise.all([
        api.get(`/me/transporter/dashboard${asParam}`),
        api.get(`/me/transporter/open-requests${asParam}`).catch(() => ({ data: { requests: [] } })),
      ]);
      setData(res.data);
      setOpenRequests(openRes.data.requests || []);
      setError(null);
    } catch (err) {
      // 403 means "not onboarded" — render friendly state with admin hint.
      setError(err?.response?.data?.detail || formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [asParam]);
  useEffect(() => { load(); }, [load]);

  // Mobile bottom nav links to `?focus=bids` — scroll the bid
  // invitations section into view once the dashboard data is in.
  const focus = searchParams.get("focus");
  useEffect(() => {
    if (!loading && data && focus === "bids") {
      const el = document.querySelector('[data-testid="bid-invitations"]');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      }
    }
  }, [loading, data, focus]);

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-5xl mx-auto px-4 py-10 space-y-4">
          <Skeleton className="h-16" /><Skeleton className="h-40" />
        </div>
      </AppShell>
    );
  }
  if (error || !data) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="transporter-not-onboarded">
          <Truck size={48} weight="duotone" className="text-[#9A9A9A] mx-auto mb-3" />
          <h1 className="font-spectral text-3xl mb-2">You're not on the roster yet</h1>
          <p className="text-[14px] text-[#6B6B6B] leading-relaxed max-w-md mx-auto">
            {error || "We couldn't find a verified transporter record matching your account email."}
          </p>
          <p className="text-[13px] text-[#6B6B6B] mt-4">
            If you've already applied, contact{" "}
            <a href="mailto:admin@armstrongk9registry.com" className="text-ink underline font-semibold">
              admin@armstrongk9registry.com
            </a>{" "}
            to expedite your onboarding.
          </p>
        </div>
      </AppShell>
    );
  }

  const t = data.transporter;
  const orders = data.orders_by_status || {};
  const active = [...(orders.booked || []), ...(orders.in_transit || [])];
  const delivered = orders.delivered || [];
  const completed = orders.completed || [];

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="transporter-dashboard">
        {/* Admin "view as" banner */}
        {asEmail && (
          <div
            className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3 flex items-center justify-between gap-3"
            data-testid="admin-view-as-banner"
          >
            <p className="text-[13px]">
              <strong>Admin view-as mode</strong> — you are viewing the dashboard for{" "}
              <strong>{t.display_name}</strong> ({asEmail}). Any actions you take run as this transporter.
            </p>
            <a
              href="/admin/transporters"
              className="text-[12px] underline whitespace-nowrap"
              data-testid="admin-exit-view-as"
            >
              Exit
            </a>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
          <h1 className="font-spectral text-4xl tracking-tight flex items-center gap-3">
            <Truck size={32} weight="duotone" className="text-ink" />
            {t.display_name}
          </h1>
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
            <ShieldCheck size={12} weight="duotone" /> Verified · {t.status}
          </span>
        </div>
        <p className="text-[13px] text-[#6B6B6B] mb-6">
          USDA {t.usda_license_number} · ${(t.insurance_liability_usd / 1000).toFixed(0)}K liability via {t.insurance_carrier}
        </p>

        {/* Stripe Connect status — surfaces "payouts ready", "finish
            onboarding", or "no account" so transporters can resume
            Stripe setup without needing the original approval email. */}
        <TransporterPayoutsBanner asEmail={asEmail} />

        {/* First-time welcome — only show if the transporter hasn't
            completed a trip yet. Surfaces the walkthrough + a clear
            next-action checklist so they don't bounce off the empty
            dashboard wondering "now what?". */}
        {(t.trips_completed ?? 0) === 0 && !asEmail && (
          <div className="mb-6 border border-crimson/20 bg-gradient-to-br from-[#FBF7EC] to-white rounded-2xl p-5" data-testid="welcome-card">
            <p className="text-[11px] uppercase tracking-wider font-bold text-crimson mb-1">
              Welcome to Armstrong Transport
            </p>
            <h2 className="font-spectral text-2xl tracking-tight mb-2">
              You're verified. Here's what's next.
            </h2>
            <ol className="text-[13px] text-[#444] space-y-1.5 mb-3 pl-5 list-decimal">
              <li>Scroll down to <strong>Preferences</strong> and pick your service states, lanes, and notification cap.</li>
              <li>Check <strong>Open bid invitations</strong> below — buyer requests covering your service area appear here.</li>
              <li>Submit competitive bids. You net 90% on every trip (Armstrong's flat 10% platform fee covers verification, escrow, and dispute mediation).</li>
              <li>Win a trip → pick up → mark delivered. Escrow auto-releases 48h after delivery.</li>
            </ol>
            <a
              href="/transport/getting-started"
              className="text-[12px] font-semibold text-crimson hover:text-ink underline underline-offset-2"
              data-testid="welcome-walkthrough-link"
              target="_blank"
              rel="noreferrer"
            >
              Read the full 8-step walkthrough →
            </a>
          </div>
        )}

        {/* Stat strip */}
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          <StatTile
            icon={<CurrencyDollar size={20} weight="duotone" />}
            label="Lifetime earnings"
            value={`$${(data.lifetime_earnings_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            tone="emerald"
            testId="stat-earnings"
          />
          <StatTile
            icon={<Truck size={20} weight="duotone" />}
            label="Trips completed"
            value={t.trips_completed ?? 0}
            tone="ink"
            testId="stat-completed"
          />
          <StatTile
            icon={<Clock size={20} weight="duotone" />}
            label="Active trips"
            value={data.open_count ?? 0}
            tone="amber"
            testId="stat-active"
          />
        </div>

        {/* Active trips — main action */}
        <BidInvitations requests={openRequests} onSubmit={load} />

        <TripSection
          title="Active trips"
          subtitle="Mark delivered when you've handed off the dog. The buyer has 48 hours to confirm, after which escrow auto-releases to you."
          trips={active}
          onChange={load}
          emptyText="No active trips. We'll notify you the second a buyer books a route you cover."
          testId="active-trips"
        />

        {/* Delivered — awaiting buyer confirm */}
        <TripSection
          title="Awaiting buyer confirm"
          subtitle="Buyer has 48 hours to confirm receipt, then escrow auto-releases."
          trips={delivered}
          isAwaiting
          emptyText=""
          testId="awaiting-trips"
        />

        {/* Completed history */}
        <TripSection
          title="Completed"
          subtitle=""
          trips={completed}
          isCompleted
          emptyText=""
          testId="completed-trips"
        />

        {/* Phase 4 — service area / lanes / blackouts / notification cap */}
        <PreferencesCard />

        {/* Legal documents — self-serve audit trail */}
        <DocumentsCard transporter={t} />
      </div>
    </AppShell>
  );
}

function StatTile({ icon, label, value, tone, testId }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    ink: "bg-[#FAFAFA] border-[#E5E5E5] text-ink",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1 opacity-70">
        {icon}
        <p className="text-[11px] uppercase tracking-wider font-bold">{label}</p>
      </div>
      <p className="font-spectral text-3xl tracking-tight">{value}</p>
    </div>
  );
}

function TripSection({ title, subtitle, trips, onChange, isAwaiting, isCompleted, emptyText, testId }) {
  if (!trips || (trips.length === 0 && !emptyText)) return null;
  return (
    <section className="mb-6" data-testid={testId}>
      <h2 className="font-spectral text-2xl tracking-tight mb-1">{title}</h2>
      {subtitle && <p className="text-[12px] text-[#6B6B6B] mb-3 max-w-2xl leading-relaxed">{subtitle}</p>}
      {trips.length === 0 ? (
        <p className="text-[13px] text-[#6B6B6B] border border-dashed border-[#E5E5E5] rounded-xl p-6 text-center">
          {emptyText}
        </p>
      ) : (
        <div className="space-y-3">
          {trips.map((o) => (
            <TripRow
              key={o.id}
              trip={o}
              onChange={onChange}
              isAwaiting={isAwaiting}
              isCompleted={isCompleted}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function TripRow({ trip, onChange, isAwaiting, isCompleted }) {
  const activeFlags = (trip.delay_flags || []).filter((f) => !f.cleared_at);
  const hasOpenDispute = trip.dispute && !trip.dispute.resolved_at;
  return (
    <div
      className="border border-[#E5E5E5] bg-white rounded-xl p-4"
      data-testid={`trip-row-${trip.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium flex items-center gap-1.5">
            <MapPin size={14} weight="duotone" className="text-ink" />
            {trip.pickup_location.city}, {trip.pickup_location.state}
            <span className="text-[#9A9A9A] mx-1">→</span>
            {trip.dropoff_location.city}, {trip.dropoff_location.state}
            {activeFlags.length > 0 && (
              <span className="text-[10px] uppercase font-bold bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 rounded ml-1">
                Delayed
              </span>
            )}
            {hasOpenDispute && (
              <span className="text-[10px] uppercase font-bold bg-red-100 border border-red-300 text-red-800 px-1.5 py-0.5 rounded ml-1">
                Disputed
              </span>
            )}
          </p>
          <p className="text-[12px] text-[#6B6B6B] mt-1">
            {trip.buyer_email}
            {trip.dog_count > 1 && ` · ${trip.dog_count} dogs`}
            {trip.requested_pickup_date && ` · pickup ${trip.requested_pickup_date}`}
          </p>
          <p className="text-[12px] font-semibold text-emerald-700 mt-1">
            You earn ${(trip.transporter_payout_usd || 0).toFixed(2)}
            {isAwaiting && trip.escrow_release_at && (
              <span className="text-[#6B6B6B] font-normal ml-1.5">
                · auto-releases {new Date(trip.escrow_release_at).toLocaleString()}
              </span>
            )}
            {isCompleted && trip.completed_at && (
              <span className="text-[#6B6B6B] font-normal ml-1.5">
                · paid {new Date(trip.completed_at).toLocaleDateString()}
              </span>
            )}
          </p>
        </div>
        {!isAwaiting && !isCompleted && (
          <MarkDeliveredButton trip={trip} onChange={onChange} />
        )}
        {isAwaiting && (
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
            <Clock size={12} /> Awaiting confirm
          </span>
        )}
        {isCompleted && (
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
            <CheckCircle size={12} /> Paid
          </span>
        )}
      </div>

      {/* Phase 6 — active action toolbar (delay / cancel / dispute).
          Only relevant on live trips (not on completed/cancelled history). */}
      {!isCompleted && !hasOpenDispute && ["booked", "in_transit", "delivered"].includes(trip.status) && (
        <TripActionToolbar trip={trip} onChange={onChange} />
      )}

      {activeFlags.length > 0 && (
        <ActiveFlagsList trip={trip} activeFlags={activeFlags} onChange={onChange} />
      )}
    </div>
  );
}

function MarkDeliveredButton({ trip, onChange }) {
  const [open, setOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!photoUrl.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/me/transporter/orders/${trip.id}/mark-delivered`, {
        delivery_photo_url: photoUrl.trim(),
        notes: notes || null,
      });
      toast.success("Marked delivered", {
        description: "Buyer notified. Escrow releases in 48 hours unless they confirm sooner.",
      });
      setOpen(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't mark delivered.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="rounded-full bg-crimson hover:bg-ink text-white text-[12px] h-9 px-4"
          data-testid={`mark-delivered-btn-${trip.id}`}
        >
          <Camera size={14} className="mr-1.5" /> Mark delivered
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark this trip as delivered</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="mark-delivered-form">
          <p className="text-[13px] text-[#6B6B6B] leading-relaxed">
            A delivery photo protects you and the buyer. Use any public
            URL (Google Drive share link, iCloud Photos share, or upload
            to <a href="https://imgur.com" target="_blank" rel="noreferrer" className="underline">Imgur</a>).
          </p>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
              Photo URL *
            </Label>
            <Input
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
              placeholder="https://..."
              required
              className="mt-1"
              data-testid="delivery-photo-url"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
              Notes for buyer (optional)
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Drop-off time, any handoff notes..."
              className="mt-1"
              data-testid="delivery-notes"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting || !photoUrl.trim()}
            className="w-full rounded-full bg-crimson hover:bg-ink text-white"
            data-testid="delivery-submit"
          >
            {submitting ? "Marking…" : "Mark delivered → start 48hr clock"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/**
 * BidInvitations — open transport requests routed to this transporter.
 *
 * Lives at the top of the dashboard so haulers see new opportunities
 * before their existing trips. Each request expands to a quote form
 * where the transporter sets their price, ETAs, and optional notes.
 */
function BidInvitations({ requests, onSubmit }) {
  if (!requests || requests.length === 0) return null;
  return (
    <section className="mb-6" data-testid="bid-invitations">
      <h2 className="font-spectral text-2xl tracking-tight mb-1 flex items-center gap-2">
        <Truck size={20} weight="duotone" className="text-crimson" />
        Open bid invitations
        <span className="text-[11px] uppercase tracking-wider font-bold text-crimson bg-[#FDECEC] border border-crimson/30 px-2 py-0.5 rounded ml-1">
          {requests.length}
        </span>
      </h2>
      <p className="text-[12px] text-[#6B6B6B] mb-3 max-w-2xl leading-relaxed">
        New requests in your service area. Bid quickly — multiple haulers see each request and the buyer picks a winner.
      </p>
      <div className="space-y-3">
        {requests.map(({ order, my_quote }) => (
          <BidInviteRow key={order.id} order={order} myQuote={my_quote} onSubmit={onSubmit} />
        ))}
      </div>
    </section>
  );
}

function BidInviteRow({ order, myQuote, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(myQuote?.amount_usd || "");
  const [pickupEta, setPickupEta] = useState(
    myQuote?.pickup_eta ? myQuote.pickup_eta.slice(0, 16) : ""
  );
  const [notes, setNotes] = useState(myQuote?.notes || "");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return;
    setSubmitting(true);
    try {
      await api.post("/me/transporter/quotes", {
        request_id: order.id,
        amount_usd: Number(amount),
        pickup_eta: pickupEta || null,
        notes: notes || null,
        quote_window_hours: 24,
      });
      toast.success(myQuote ? "Bid updated" : "Bid submitted", {
        description: "Buyer has been notified.",
      });
      setOpen(false);
      onSubmit?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't submit bid.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="border border-[#E5E5E5] bg-white rounded-xl p-4"
      data-testid={`bid-invite-row-${order.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium flex items-center gap-1.5">
            <MapPin size={14} weight="duotone" className="text-ink" />
            {order.pickup_location.city}, {order.pickup_location.state}
            <span className="text-[#9A9A9A] mx-1">→</span>
            {order.dropoff_location.city}, {order.dropoff_location.state}
          </p>
          <p className="text-[12px] text-[#6B6B6B] mt-0.5">
            {order.dog_count} dog{order.dog_count > 1 ? "s" : ""}
            {order.breed_size && ` · ${order.breed_size}`}
            {order.requested_pickup_date && ` · pickup ${order.requested_pickup_date}`}
            {order.flexibility_days > 0 && ` (±${order.flexibility_days}d)`}
          </p>
          {myQuote && (
            <p className="text-[12px] font-semibold text-emerald-700 mt-1">
              Your bid: ${myQuote.amount_usd.toFixed(2)} ({myQuote.status})
            </p>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => setOpen(!open)}
          className={`rounded-full text-[12px] h-9 px-4 ${
            myQuote
              ? "bg-ink hover:bg-crimson text-white"
              : "bg-crimson hover:bg-ink text-white"
          }`}
          data-testid={`bid-toggle-${order.id}`}
        >
          {open ? "Cancel" : myQuote ? "Update bid" : "Submit bid"}
        </Button>
      </div>
      {open && (
        <form onSubmit={submit} className="mt-3 pt-3 border-t border-[#EAEAEA] space-y-3" data-testid={`bid-form-${order.id}`}>
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Your price (USD)</Label>
              <Input
                type="number"
                step="0.01"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                className="mt-1"
                placeholder="375.00"
                data-testid={`bid-amount-${order.id}`}
              />
              <p className="text-[10px] text-[#9A9A9A] mt-1">
                You net ${(Number(amount) * 0.9 || 0).toFixed(2)} after 10% platform fee.
              </p>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Pickup ETA</Label>
              <Input
                type="datetime-local"
                value={pickupEta}
                onChange={(e) => setPickupEta(e.target.value)}
                className="mt-1"
                data-testid={`bid-eta-${order.id}`}
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Notes for buyer (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1"
              placeholder="Crate type, schedule constraints, what's included..."
              data-testid={`bid-notes-${order.id}`}
            />
          </div>
          <Button
            type="submit"
            disabled={submitting || !amount}
            className="rounded-full bg-crimson hover:bg-ink text-white px-6"
            data-testid={`bid-submit-${order.id}`}
          >
            {submitting ? "Submitting…" : myQuote ? "Update bid" : "Submit bid"}
          </Button>
        </form>
      )}
    </div>
  );
}


/**
 * TripActionToolbar — per-trip Flag delay / Cancel / Dispute actions.
 *
 * Rendered below the main TripRow content on active trips. Each
 * action opens its own dialog with the appropriate fields.
 */
function TripActionToolbar({ trip, onChange }) {
  const [flagOpen, setFlagOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  return (
    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#EAEAEA]" data-testid={`trip-toolbar-${trip.id}`}>
      <Button
        variant="outline" size="sm"
        onClick={() => setFlagOpen(true)}
        className="rounded-full text-[11px] h-8 px-3 border-amber-300 text-amber-800 hover:bg-amber-50"
        data-testid={`flag-delay-${trip.id}`}
      >
        <Clock size={12} className="mr-1" /> Flag delay
      </Button>
      <Button
        variant="outline" size="sm"
        onClick={() => setCancelOpen(true)}
        className="rounded-full text-[11px] h-8 px-3"
        data-testid={`trip-cancel-${trip.id}`}
      >
        Cancel trip
      </Button>
      <Button
        variant="outline" size="sm"
        onClick={() => setDisputeOpen(true)}
        className="rounded-full text-[11px] h-8 px-3 border-red-300 text-red-700 hover:bg-red-50"
        data-testid={`trip-dispute-${trip.id}`}
      >
        Open dispute
      </Button>
      <FlagDelayDialog open={flagOpen} onOpenChange={setFlagOpen} trip={trip} onChange={onChange} />
      <TransporterCancelDialog open={cancelOpen} onOpenChange={setCancelOpen} trip={trip} onChange={onChange} />
      <TransporterDisputeDialog open={disputeOpen} onOpenChange={setDisputeOpen} trip={trip} onChange={onChange} />
    </div>
  );
}

function FlagDelayDialog({ open, onOpenChange, trip, onChange }) {
  const [reason, setReason] = useState("weather");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/me/transporter/orders/${trip.id}/flag-delay`,
        { reason, note: note || null });
      toast.success("Delay flagged", { description: "Auto-release paused. Buyer notified." });
      onOpenChange(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't flag delay.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Flag a delivery delay</DialogTitle></DialogHeader>
        <p className="text-[13px] text-[#6B6B6B] leading-relaxed mb-3">
          Each flag extends the escrow auto-release clock by 24 hours and notifies the buyer.
          Clear the flag from this trip once the issue resolves.
        </p>
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Reason</Label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-1 mb-3 w-full border border-[#E5E5E5] rounded-md h-9 px-3 text-[13px] bg-white"
          data-testid="flag-reason-select"
        >
          <option value="weather">Weather / road conditions</option>
          <option value="unreachable">Buyer unreachable</option>
          <option value="reschedule">Buyer requested reschedule</option>
          <option value="mechanical">Mechanical issue</option>
          <option value="other">Other</option>
        </select>
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Note (optional)</Label>
        <Textarea
          value={note} onChange={(e) => setNote(e.target.value)}
          rows={3} className="mt-1" placeholder="Quick context for the buyer…"
          data-testid="flag-note"
        />
        <Button onClick={submit} disabled={submitting}
          className="w-full mt-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="flag-submit"
        >
          {submitting ? "Flagging…" : "Flag delay (+24h to clock)"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function TransporterCancelDialog({ open, onOpenChange, trip, onChange }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 4) {
      toast.error("Please provide a brief reason.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/me/transporter/orders/${trip.id}/cancel`, { reason });
      toast.success(
        res.data.short_notice
          ? `Cancelled — short-notice strike issued (${res.data.strike_count}/3).`
          : "Cancelled — full refund to buyer, no strike.",
      );
      onOpenChange(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't cancel.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Cancel this trip?</DialogTitle></DialogHeader>
        <p className="text-[13px] text-[#6B6B6B] leading-relaxed mb-2">
          Buyers always get a full refund on transporter-cancelled trips. Lead-time policy:
        </p>
        <ul className="text-[12px] text-[#444] space-y-1 mb-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
          <li>· <strong>≥12 hours notice</strong>: no penalty</li>
          <li>· <strong>&lt;12 hours notice</strong>: +1 strike (3 strikes = suspension)</li>
        </ul>
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Reason *</Label>
        <Textarea
          value={reason} onChange={(e) => setReason(e.target.value)}
          rows={3} className="mt-1"
          placeholder="Truck issue, family emergency, conflict, etc."
          data-testid="t-cancel-reason"
        />
        <Button onClick={submit} disabled={submitting}
          className="w-full mt-3 rounded-full bg-crimson hover:bg-ink text-white"
          data-testid="t-cancel-submit"
        >
          {submitting ? "Cancelling…" : "Confirm cancellation"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function TransporterDisputeDialog({ open, onOpenChange, trip, onChange }) {
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/me/transporter/orders/${trip.id}/dispute`, {
        reason,
        evidence_urls: evidence.split(/\s|,/).map((s) => s.trim()).filter(Boolean).slice(0, 10),
      });
      toast.success("Dispute opened — escrow frozen pending Armstrong review.");
      onOpenChange(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't open dispute.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Open a dispute</DialogTitle></DialogHeader>
        <p className="text-[13px] text-[#6B6B6B] leading-relaxed mb-3">
          Use when buyer is unreachable to confirm, or claims a non-delivery you can prove happened.
          Armstrong reviews within 72 business hours.
        </p>
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">What happened? *</Label>
        <Textarea
          value={reason} onChange={(e) => setReason(e.target.value)}
          rows={4} className="mt-1 mb-3"
          data-testid="t-dispute-reason"
        />
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Evidence URLs (optional)</Label>
        <Textarea
          value={evidence} onChange={(e) => setEvidence(e.target.value)}
          rows={2} className="mt-1"
          placeholder="https://imgur.com/..."
          data-testid="t-dispute-evidence"
        />
        <Button onClick={submit} disabled={submitting}
          className="w-full mt-3 rounded-full bg-red-600 hover:bg-red-700 text-white"
          data-testid="t-dispute-submit"
        >
          {submitting ? "Opening…" : "Open dispute (freezes escrow)"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ActiveFlagsList({ trip, activeFlags, onChange }) {
  const clear = async (flagId) => {
    if (!window.confirm("Clear this flag? Auto-release resumes once all flags are clear.")) return;
    try {
      await api.post(`/me/transporter/orders/${trip.id}/clear-delay-flag/${flagId}`);
      toast.success("Flag cleared.");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't clear flag.");
    }
  };
  return (
    <div className="mt-3 pt-3 border-t border-[#EAEAEA]" data-testid={`active-flags-${trip.id}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1">
        Active delay flags
      </p>
      {activeFlags.map((f) => (
        <div key={f.id} className="flex items-start justify-between gap-2 text-[12px] text-[#444] py-1">
          <span>
            · <strong className="capitalize">{f.reason}</strong>
            {f.note && <span> — {f.note}</span>}
            <span className="text-[#9A9A9A] ml-1">
              ({new Date(f.raised_at).toLocaleString()})
            </span>
          </span>
          <button
            onClick={() => clear(f.id)}
            className="text-[11px] underline text-emerald-700 hover:text-emerald-900"
            data-testid={`clear-flag-${f.id}`}
          >
            Clear
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * PreferencesCard — Phase 4 transporter settings.
 *
 * GET /api/me/transporter/preferences synthesizes defaults from the
 * `transporters` row when no prefs doc exists yet. PUT mirrors state
 * + breed-familiarity back onto the `transporters` row so the bid
 * routing engine + public directory's filter pick up changes
 * immediately (no second-collection join on the hot path).
 */
const BREED_SIZES = ["toy", "small", "medium", "large", "giant"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function PreferencesCard() {
  const [prefs, setPrefs] = useState(null);
  const [lanes, setLanes] = useState("");
  const [blackouts, setBlackouts] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/transporter/preferences")
      .then((r) => {
        const p = r.data.preferences;
        setPrefs(p);
        setLanes((p.preferred_lanes || []).join("\n"));
        setBlackouts((p.blackout_dates || []).join(", "));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !prefs) {
    return <Skeleton className="h-40 mt-6" />;
  }

  const toggleState = (s) => {
    setPrefs((p) => ({
      ...p,
      service_area_states: p.service_area_states.includes(s)
        ? p.service_area_states.filter((x) => x !== s)
        : [...p.service_area_states, s].sort(),
    }));
  };
  const toggleBreed = (b) => {
    setPrefs((p) => ({
      ...p,
      breed_familiarity: (p.breed_familiarity || []).includes(b)
        ? p.breed_familiarity.filter((x) => x !== b)
        : [...(p.breed_familiarity || []), b],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        service_area_states: prefs.service_area_states,
        preferred_lanes: lanes.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 20),
        breed_familiarity: prefs.breed_familiarity || [],
        min_trip_miles: prefs.min_trip_miles ? Number(prefs.min_trip_miles) : null,
        max_trip_miles: prefs.max_trip_miles ? Number(prefs.max_trip_miles) : null,
        blackout_dates: blackouts.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean).slice(0, 120),
        daily_notification_cap: Number(prefs.daily_notification_cap) || 20,
        quote_window_minutes: Number(prefs.quote_window_minutes) || 1440,
      };
      const res = await api.put("/me/transporter/preferences", payload);
      setPrefs(res.data.preferences);
      toast.success("Preferences saved", {
        description: "Future bid invitations will use these settings.",
      });
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-8 border border-[#E5E5E5] bg-white rounded-2xl p-5" data-testid="preferences-card">
      <h2 className="font-spectral text-2xl tracking-tight mb-1">Preferences</h2>
      <p className="text-[12px] text-[#6B6B6B] mb-4 max-w-2xl leading-relaxed">
        Control which routes we route to you and how often. Service area is mirrored to your public profile.
      </p>

      <div className="mb-4">
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Service area states</Label>
        <div className="flex flex-wrap gap-1 mt-2" data-testid="state-chips">
          {US_STATES.map((s) => {
            const on = prefs.service_area_states.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleState(s)}
                className={`text-[11px] h-7 w-9 rounded-md border transition-colors ${
                  on
                    ? "bg-ink border-ink text-white"
                    : "bg-white border-[#E5E5E5] text-[#444] hover:bg-[#FAFAFA]"
                }`}
                data-testid={`state-chip-${s}`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Breed familiarity</Label>
        <div className="flex gap-1 mt-2" data-testid="breed-chips">
          {BREED_SIZES.map((b) => {
            const on = (prefs.breed_familiarity || []).includes(b);
            return (
              <button
                key={b}
                type="button"
                onClick={() => toggleBreed(b)}
                className={`text-[11px] h-8 px-3 rounded-full border capitalize transition-colors ${
                  on
                    ? "bg-ink border-ink text-white"
                    : "bg-white border-[#E5E5E5] text-[#444] hover:bg-[#FAFAFA]"
                }`}
                data-testid={`breed-chip-${b}`}
              >
                {b}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Min trip miles</Label>
          <Input
            type="number" min="0" value={prefs.min_trip_miles || ""}
            onChange={(e) => setPrefs((p) => ({ ...p, min_trip_miles: e.target.value }))}
            className="mt-1" placeholder="e.g. 50"
            data-testid="pref-min-miles"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Max trip miles</Label>
          <Input
            type="number" min="0" value={prefs.max_trip_miles || ""}
            onChange={(e) => setPrefs((p) => ({ ...p, max_trip_miles: e.target.value }))}
            className="mt-1" placeholder="e.g. 1500"
            data-testid="pref-max-miles"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Daily notification cap</Label>
          <Input
            type="number" min="1" max="200"
            value={prefs.daily_notification_cap}
            onChange={(e) => setPrefs((p) => ({ ...p, daily_notification_cap: e.target.value }))}
            className="mt-1"
            data-testid="pref-cap"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Quote window (minutes)</Label>
          <Input
            type="number" min="30" max="10080"
            value={prefs.quote_window_minutes}
            onChange={(e) => setPrefs((p) => ({ ...p, quote_window_minutes: e.target.value }))}
            className="mt-1"
            data-testid="pref-window"
          />
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Preferred lanes (one per line)</Label>
        <Textarea
          value={lanes} onChange={(e) => setLanes(e.target.value)}
          rows={3} className="mt-1 font-mono text-[12px]"
          placeholder="Memphis,TN → Atlanta,GA"
          data-testid="pref-lanes"
        />
        <p className="text-[10px] text-[#9A9A9A] mt-1">
          Free-text — these surface on your public profile to attract buyers in your sweet-spot routes.
        </p>
      </div>

      <div className="mb-4">
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Blackout dates (comma-separated)</Label>
        <Textarea
          value={blackouts} onChange={(e) => setBlackouts(e.target.value)}
          rows={2} className="mt-1 font-mono text-[12px]"
          placeholder="2026-12-25, 2027-01-01"
          data-testid="pref-blackouts"
        />
      </div>

      <Button onClick={save} disabled={saving}
        className="rounded-full bg-crimson hover:bg-ink text-white px-6"
        data-testid="pref-save">
        {saving ? "Saving…" : "Save preferences"}
      </Button>
    </section>
  );
}



/**
 * Legal documents card on the transporter dashboard.
 *
 * Three sections:
 *   1. Your signed copy — personalized PDF with legal name + IP +
 *      accept-timestamp + version stamp on the cover. Only renders if
 *      we have `approved_from_application_id` to query by.
 *   2. Reference: Transporter Agreement (current public version).
 *   3. Reference: Transporter FAQ (operational walkthrough).
 *
 * Audit-trail purpose: closes the loop so the transporter can pull
 * their own signed copy at any time without emailing support.
 */
function DocumentsCard({ transporter }) {
  const t = transporter || {};
  const applicationId = t.approved_from_application_id;
  const executedHref = applicationId
    ? `/api/me/consents/executed.pdf?kind=transporter_agreement&application_id=${encodeURIComponent(applicationId)}`
    : null;

  const refDocs = [
    {
      label: "Transporter Agreement",
      version: "v1.0",
      desc: "11-article click-wrap covering relationship, eligibility, platform fee, escrow, cancellation tiers, disputes, indemnification, and termination.",
      preview: "/api/transport/docs/agreement.pdf",
      testid: "tx-docs-agreement",
    },
    {
      label: "Transporter FAQ",
      version: "v2026",
      desc: "Operational walkthrough — bidding, escrow timing, mark-delivered photo rules, payout schedule, dispute path.",
      preview: "/api/transport/docs/faq.pdf",
      testid: "tx-docs-faq",
    },
  ];

  return (
    <section
      className="mt-8 border border-[#E5E5E5] bg-white rounded-2xl p-5"
      data-testid="transporter-docs-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <FilePdf size={20} weight="duotone" className="text-crimson flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <h2 className="font-spectral text-2xl tracking-tight">Documents</h2>
          <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-snug">
            Your signed agreement + reference copies of every public document. Download anytime — for your records, your lawyer, your insurance broker, or your accountant.
          </p>
        </div>
      </div>

      {executedHref && (
        <div
          className="mb-4 rounded-lg border border-[#0F8043]/40 bg-[#EBF7EE] p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="transporter-docs-executed"
        >
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-[#0F8043] font-bold flex items-center gap-1">
              <ShieldCheck size={11} weight="fill" /> Your signed copy
            </p>
            <p className="text-[13px] text-ink font-semibold leading-tight mt-1">
              Executed Transporter Agreement
            </p>
            <p className="text-[11.5px] text-[#3F3F3F] mt-0.5 leading-snug">
              Personalized PDF — your legal name, IP, accept-timestamp, and version stamped on the cover.
            </p>
          </div>
          <a
            href={executedHref}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-[#0F8043] hover:bg-[#0a6635] text-white transition-colors flex-shrink-0"
            data-testid="transporter-docs-executed-download"
          >
            <DownloadSimple size={12} weight="bold" /> Download executed copy
          </a>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {refDocs.map((d) => (
          <div
            key={d.testid}
            className="border border-[#EAEAEA] bg-white rounded-lg p-3 flex flex-col"
            data-testid={d.testid}
          >
            <p className="font-semibold text-ink text-[13.5px] leading-tight">{d.label}</p>
            <p className="text-[11px] uppercase tracking-wider text-gold font-bold mt-0.5">{d.version}</p>
            <p className="text-[12px] text-[#6B6B6B] mt-1.5 leading-snug flex-1">{d.desc}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <a
                href={d.preview}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold border border-[#EAEAEA] hover:border-gold/55 text-ink hover:bg-cream/40 transition-colors"
                data-testid={`${d.testid}-preview`}
              >
                Preview
              </a>
              <a
                href={d.preview}
                download
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-ink hover:bg-crimson text-white transition-colors"
                data-testid={`${d.testid}-download`}
              >
                <DownloadSimple size={12} weight="bold" /> Download PDF
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
